import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import NotificationBell from './NotificationBell';
import { api } from '../../lib/api';

interface TopBarProps {
  title: string;
}

interface SearchResult {
  type: 'client' | 'sale' | 'lot' | 'section' | 'agent' | 'payment';
  id: string;
  title: string;
  subtitle: string;
  badge: string;
  link: string;
  meta?: Record<string, any>;
}

const TYPE_CONFIG: Record<string, { icon: string; color: string; label: string }> = {
  client:  { icon: 'solar:user-bold-duotone',              color: '#3B82F6', label: 'Clients' },
  sale:    { icon: 'solar:sale-bold-duotone',               color: '#0D9488', label: 'Sales' },
  lot:     { icon: 'solar:map-point-bold-duotone',          color: '#F59E0B', label: 'Lots' },
  section: { icon: 'solar:map-bold-duotone',                color: '#8B5CF6', label: 'Sections' },
  agent:   { icon: 'solar:users-group-rounded-bold-duotone', color: '#EC4899', label: 'Users' },
  payment: { icon: 'solar:wallet-money-bold-duotone',       color: '#10B981', label: 'Payments' },
};

const TopBar: React.FC<TopBarProps> = ({ title }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced search
  const doSearch = useCallback(async (q: string) => {
    if (q.length < 2) { setResults([]); setOpen(false); return; }
    setLoading(true);
    try {
      const data = await api.search(q);
      setResults(data.results || []);
      setOpen(true);
      setActiveIdx(-1);
    } catch { setResults([]); }
    finally { setLoading(false); }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), 300);
  };

  // Navigate to result
  const goTo = useCallback((r: SearchResult) => {
    setOpen(false);
    setQuery('');
    setResults([]);
    navigate(r.link, { state: r.meta });
  }, [navigate]);

  // Keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) {
      if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx(prev => (prev + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx(prev => (prev <= 0 ? results.length - 1 : prev - 1));
    } else if (e.key === 'Enter' && activeIdx >= 0) {
      e.preventDefault();
      goTo(results[activeIdx]);
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Ctrl+K / Cmd+K shortcut to focus search
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // Group results by type
  const grouped = results.reduce<Record<string, SearchResult[]>>((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {});

  // Flat list for keyboard index
  const flatResults = Object.values(grouped).flat();

  return (
    <header style={{
      height: '64px',
      background: 'var(--nav-bg)',
      backdropFilter: 'blur(16px)',
      WebkitBackdropFilter: 'blur(16px)',
      borderBottom: '1px solid var(--nav-border)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 28px',
      position: 'sticky',
      top: 0,
      zIndex: 50,
    }}>
      <h1 style={{
        fontFamily: 'var(--font-heading)',
        fontSize: '20px',
        fontWeight: 600,
        color: 'var(--text-primary)',
        margin: 0,
      }}>
        {title}
      </h1>

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Global Search */}
        <div ref={dropdownRef} style={{ position: 'relative' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: open ? 'var(--bg-elevated)' : 'var(--input-bg)',
            border: `1px solid ${open ? 'var(--border-active)' : 'var(--input-border)'}`,
            borderRadius: 'var(--radius-md)',
            padding: '8px 14px',
            minWidth: '280px',
            transition: 'all 0.2s ease',
          }}>
            {loading ? (
              <Icon icon="solar:refresh-bold-duotone" width={16}
                style={{ color: 'var(--primary-400)', animation: 'spin 1s linear infinite', flexShrink: 0 }} />
            ) : (
              <Icon icon="solar:magnifer-bold-duotone" width={16}
                style={{ color: open ? 'var(--primary-400)' : 'var(--text-muted)', flexShrink: 0 }} />
            )}
            <input
              ref={inputRef}
              value={query}
              onChange={handleChange}
              onFocus={() => { if (results.length > 0) setOpen(true); }}
              onKeyDown={handleKeyDown}
              placeholder="Search everything..."
              style={{
                background: 'none',
                border: 'none',
                outline: 'none',
                color: 'var(--input-text)',
                fontSize: '13px',
                fontFamily: 'var(--font-body)',
                width: '100%',
              }}
            />
            <kbd style={{
              fontSize: '10px',
              color: 'var(--text-faint)',
              background: 'var(--bg-elevated)',
              border: '1px solid var(--border-base)',
              borderRadius: '4px',
              padding: '2px 6px',
              fontFamily: 'var(--font-mono)',
              flexShrink: 0,
              lineHeight: 1,
            }}>⌘K</kbd>
          </div>

          {/* Search Results Dropdown */}
          {open && (
            <div style={{
              position: 'absolute',
              top: 'calc(100% + 6px)',
              right: 0,
              width: '420px',
              maxHeight: '480px',
              overflowY: 'auto',
              background: 'var(--modal-bg)',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: '1px solid var(--border-base)',
              borderRadius: 'var(--radius-lg)',
              boxShadow: 'var(--modal-shadow)',
              zIndex: 200,
              scrollbarWidth: 'thin',
            }}>
              {results.length === 0 && query.length >= 2 && !loading && (
                <div style={{
                  padding: '32px 20px',
                  textAlign: 'center',
                }}>
                  <Icon icon="solar:magnifer-zoom-in-bold-duotone" width={32}
                    style={{ color: 'var(--text-faint)', marginBottom: '8px' }} />
                  <div style={{ color: 'var(--text-muted)', fontSize: '13px' }}>
                    No results for "<strong>{query}</strong>"
                  </div>
                  <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '4px' }}>
                    Try searching by name, lot number, email, or phone
                  </div>
                </div>
              )}

              {results.length === 0 && query.length < 2 && (
                <div style={{
                  padding: '24px 20px',
                  textAlign: 'center',
                }}>
                  <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                    Type at least 2 characters to search
                  </div>
                </div>
              )}

              {Object.entries(grouped).map(([type, items]) => {
                const cfg = TYPE_CONFIG[type] || { icon: 'solar:document-bold-duotone', color: 'var(--text-muted)', label: type };
                return (
                  <div key={type}>
                    {/* Category Header */}
                    <div style={{
                      padding: '10px 16px 6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      position: 'sticky',
                      top: 0,
                      background: 'var(--modal-bg)',
                      backdropFilter: 'blur(24px)',
                      zIndex: 1,
                    }}>
                      <Icon icon={cfg.icon} width={13} style={{ color: cfg.color }} />
                      <span style={{
                        fontSize: '10px',
                        fontWeight: 700,
                        color: cfg.color,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                      }}>
                        {cfg.label}
                      </span>
                      <span style={{
                        fontSize: '10px',
                        color: 'var(--text-faint)',
                        marginLeft: 'auto',
                      }}>
                        {items.length}
                      </span>
                    </div>

                    {/* Results */}
                    {items.map((r) => {
                      const flatIdx = flatResults.indexOf(r);
                      const isActive = flatIdx === activeIdx;
                      return (
                        <button
                          key={r.id}
                          onClick={() => goTo(r)}
                          onMouseEnter={() => setActiveIdx(flatIdx)}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '10px 16px',
                            border: 'none',
                            background: isActive ? 'var(--nav-item-active-bg)' : 'transparent',
                            cursor: 'pointer',
                            textAlign: 'left',
                            fontFamily: 'var(--font-body)',
                            transition: 'background 0.1s ease',
                          }}
                        >
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: `${cfg.color}15`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            <Icon icon={cfg.icon} width={16} style={{ color: cfg.color }} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              color: 'var(--text-primary)',
                              fontSize: '13px',
                              fontWeight: 600,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {highlightMatch(r.title, query)}
                            </div>
                            <div style={{
                              color: 'var(--text-muted)',
                              fontSize: '11px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              marginTop: '1px',
                            }}>
                              {r.subtitle}
                            </div>
                          </div>
                          {r.badge && (
                            <span style={{
                              fontSize: '9px',
                              fontWeight: 700,
                              padding: '2px 6px',
                              borderRadius: '4px',
                              background: `${cfg.color}15`,
                              color: cfg.color,
                              textTransform: 'uppercase',
                              letterSpacing: '0.04em',
                              flexShrink: 0,
                            }}>
                              {r.badge}
                            </span>
                          )}
                          <Icon icon="solar:arrow-right-bold" width={12}
                            style={{ color: 'var(--text-faint)', flexShrink: 0, opacity: isActive ? 1 : 0 }} />
                        </button>
                      );
                    })}
                  </div>
                );
              })}

              {/* Footer hint */}
              {results.length > 0 && (
                <div style={{
                  padding: '8px 16px',
                  borderTop: '1px solid var(--border-base)',
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: 'var(--text-faint)' }}>
                    <kbd style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-base)', borderRadius: '3px', padding: '1px 4px', fontSize: '9px', fontFamily: 'var(--font-mono)' }}>↑↓</kbd>
                    navigate
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: 'var(--text-faint)' }}>
                    <kbd style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-base)', borderRadius: '3px', padding: '1px 4px', fontSize: '9px', fontFamily: 'var(--font-mono)' }}>↵</kbd>
                    open
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '10px', color: 'var(--text-faint)' }}>
                    <kbd style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-base)', borderRadius: '3px', padding: '1px 4px', fontSize: '9px', fontFamily: 'var(--font-mono)' }}>esc</kbd>
                    close
                  </span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Notification Bell */}
        <NotificationBell />

        {/* Avatar */}
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: 'var(--radius-md)',
          background: 'rgba(13, 148, 136, 0.20)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'var(--primary-400)',
          fontWeight: 700,
          fontSize: '14px',
          fontFamily: 'var(--font-heading)',
          cursor: 'pointer',
        }}>
          A
        </div>
      </div>
    </header>
  );
};

// Highlight matching text in results
function highlightMatch(text: string, query: string): React.ReactNode {
  if (!query || query.length < 2) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <span style={{ color: 'var(--primary-400)', fontWeight: 700 }}>{text.slice(idx, idx + query.length)}</span>
      {text.slice(idx + query.length)}
    </>
  );
}

export default TopBar;
